// Plain-language helpers for the MSO engine comparison (Q-54). Pure functions, so the sentences a Product Owner
// reads are tested without mounting a page.
import { formatCents } from './format'
import { humaniseToken } from './mso-vocabulary'
import type { MsoBasis, MsoBasisSource } from './types'

/** Where the facts behind a comparison came from. A demonstration row must never read as a fact about a real bill. */
export const BASIS_SOURCE_LABEL: Readonly<Record<MsoBasisSource, string>> = {
  DEMONSTRATION: 'Demonstration data (not a fact about this bill)',
  OFFICER_ENTRY: 'Recorded by a PSD officer',
  HSP_FILE: 'Supplied on the provider file',
}

export const BASIS_SOURCE_SHORT: Readonly<Record<MsoBasisSource, string>> = {
  DEMONSTRATION: 'Demonstration',
  OFFICER_ENTRY: 'Officer entry',
  HSP_FILE: 'Provider file',
}

/** "Principal, outpatient follow-up at a specialist" — the facts, as a sentence a PSD officer would say. */
export function describeBasis(basis: MsoBasis): string {
  const who = basis.beneficiaryType === 'PRINCIPAL' ? 'Principal' : 'Dependant'
  if (basis.careSetting === 'INPATIENT') {
    const condition =
      basis.specialBillCondition === 'NONE' ? 'no special condition' : humaniseToken(basis.specialBillCondition).toLowerCase()
    const upgrade = basis.voluntaryWardUpgrade === true ? ', voluntary ward upgrade' : ''
    return `${who}, inpatient, ${condition}${upgrade}`
  }
  const visit = basis.visitType === null ? 'visit' : humaniseToken(basis.visitType).toLowerCase()
  const where = basis.hspType === null ? '' : ` at a ${humaniseToken(basis.hspType).toLowerCase()}`
  const medisave = basis.billIndicator === 'MEDISAVE' ? ', Medisave' : ''
  return `${who}, outpatient ${visit}${where}${medisave}`
}

/** A signed dollar change that says which way it goes in words as well as a sign: never colour alone. */
export function describeChange(cents: number | null): string {
  if (cents === null) return 'Not comparable'
  if (cents === 0) return 'No change'
  return cents > 0 ? `Government pays ${formatCents(cents)} more` : `Government pays ${formatCents(-cents)} less`
}

/** The short form for a table cell. */
export function shortChange(cents: number | null): string {
  if (cents === null) return 'Not comparable'
  if (cents === 0) return 'No change'
  return `${cents > 0 ? '+' : '-'}${formatCents(Math.abs(cents))}`
}
